import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  familyLifeStoryPdfPath,
  breakGlass,
  cognitiveAssessments,
  ehrPush,
  ehrStatus,
  fhirCarePlan,
  fhirObservations,
  fhirPatient,
  fallRiskTrajectory,
  guardianSignalFeedback,
  guardianSignalStep,
  guardianSignals,
  medPassAdminister,
  mobilityInterventionAdd,
  mobilityInterventions,
  partAStay,
  partAStaySet,
  residentAcp,
  residentAdl,
  residentCarePlan,
  residentCarePlanGoalPatch,
  residentChart,
  residentCondition,
  residentConditionAck,
  residentConditionClose,
  residentConversations,
  residentDetail,
  residentDirectiveAdd,
  residentDirectives,
  residentEscalate,
  residentFacesheet,
  residentFamilyCode,
  residentFamilySharing,
  residentFamilySharingSave,
  residentImmunizationRecord,
  residentImmunizations,
  residentMds,
  residentMeasureCapture,
  residentMedications,
  residentMedsToday,
  residentMemories,
  residentDiagnosisAdd,
  residentPayerSet,
  residentPdpm,
  residentReminders,
  residentSbar,
  residentScore,
  residentTaskOrder,
  residentTimeline,
  residentTimelineAdd,
  residentTransferAdd,
  residentTransfers,
  residentVitals,
  residentMedWatches,
  medWatchStepDone,
  medWatchClose,
  medWatchSign,
  residentDevices,
  deviceConnect,
  deviceDisconnect,
  junctionLink,
  type ApiError,
  type FhirResource,
  type ShareCategory,
  type WatchMovementResponse,
  type WatchRow,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { CogRing } from '../../ui/charts';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { ErrorState } from '../../ui/states';
import { type ResidentsCtx, useApi, useNow } from './common';
import { TeamAccessBanner, TeamAccessRequest, isTeamRestricted } from './TeamAccess';
import { chartVals, type ChartUi, type ComposeMode } from './chartVals';
import { measuresVals, recordVals, vitalsVals, type MeasuresUi } from './chartTabs';
import { ChartPaneView } from './ChartPaneView';
import { FamilyLinks, FamilyRequests } from './FamilyRequests';
import { HealthCardCard } from './ChartExtras';
import { medWatchVals, type MedWatchUi } from './medWatchVals';
import { devicesVals, veteranVals } from './chartExtraVals';
import { ReminderControls } from '../../ui/ReminderControls';
import { MedicationAdd } from './MedicationAdd';

export type ChartTabKey = 'overview' | 'vitals' | 'measures' | 'chart';

const SHARE_KEYS: ShareCategory[] = ['wellbeing', 'moodTrend', 'highlights', 'lifeStory', 'medicalStatus'];
const WRITE_KIND: Record<Exclude<ComposeMode, ''>, string> = { note: 'NOTE', transfer: 'TRANSFER', escalate: 'ESCALATION', task: 'TASK' };

/** The resident's chart (design rwChart, T2113-3099). */
export function ChartPane(p: {
  ctx: ResidentsCtx;
  rows: WatchRow[];
  watch: WatchMovementResponse | null;
  selId: string;
  focusMissing: string | null;
  onSelect: (id: string) => void;
  onBack: () => void;
  clearFocus: () => void;
  tab: ChartTabKey;
  setTab: (t: ChartTabKey) => void;
  query: string;
  setQuery: (v: string) => void;
}) {
  const { ctx: ctx0, selId: id, tab } = p;
  // The chart is read-only for the viewer when the role is (Partner, View as), when Aithora
  // reads a building (the Roster's own rule), or when the watch list says so: then no write
  // action is offered at all, rather than one the server refuses.
  const ctx = useMemo(() => ({ ...ctx0, readOnly: ctx0.readOnly || ctx0.persona === 'Operator' || !!p.watch?.readOnly }), [ctx0, p.watch?.readOnly]);
  const { session } = useSession();
  const navigate = useNavigate();
  const nowMs = useNow();
  const row = p.rows.find((r) => r.residentId === id) ?? null;
  // The resident's own building, never the viewer's: Aithora's team sits in Aithora's own
  // organization, which holds no residents (QA-07). A chart opened from another list (Care
  // Plans) may have no roster row: the resident record names the building, and Aithora waits
  // for it rather than asking with its own organization.
  const team = ctx.persona === 'Operator';
  const detail = useApi((c, signal) => residentDetail(c, { params: { id }, signal }), [id]);
  const orgId = row?.facilityId ?? detail.data?.resident.organization_id ?? (team ? undefined : ctx.orgId ?? undefined);
  const [err, setErr] = useState<ApiError | null>(null);
  const [ui, setUiState] = useState<ChartUi>({
    filter: 'All', compose: '', draft: '', writes: [], breakGlass: false, sbarOpen: false, sbarText: {}, carePath: '',
    methodOpen: false, logNote: '', directive: '', directiveToday: false, signalOutcome: {},
  });
  const [mui, setMui] = useState<MeasuresUi>({ domain: 'all', track: 'all', query: '' });
  const [goalsDone, setGoalsDone] = useState<Record<string, boolean>>({});
  const [sharingDraft, setSharingDraft] = useState<Record<string, boolean> | null>(null);
  const [statusDraft, setStatusDraft] = useState<'STABLE' | 'RECOVERING' | 'WATCHING' | 'CALL' | null | undefined>(undefined);
  const [noteDraft, setNoteDraft] = useState<string | undefined>(undefined);
  const setUi = (x: Partial<ChartUi>) => setUiState((s) => ({ ...s, ...x }));
  const dep = [id];

  // Always: the head, the banner and the Overview.
  const facesheet = useApi((c, signal) => residentFacesheet(c, { params: { id }, signal }), dep);
  // Building-scoped reads wait for the resident's building (never sent with the viewer's own).
  const orgKnown = !team || !!orgId;
  const fall = useApi((c, signal) => fallRiskTrajectory(c, { query: { userId: id, organizationId: orgId }, signal }), [id, orgId], { enabled: orgKnown });
  const interventions = useApi((c, signal) => mobilityInterventions(c, { query: { residentId: id, organizationId: orgId }, signal }), [id, orgId], { enabled: orgKnown });
  const cognitive = useApi((c, signal) => cognitiveAssessments(c, { query: { userId: id }, signal }), dep);
  const meds = useApi((c, signal) => residentMedications(c, { query: { userId: id, organizationId: orgId }, signal }), [id, orgId], { enabled: orgKnown });
  const medsToday = useApi((c, signal) => residentMedsToday(c, { params: { id }, signal }), dep);
  const memories = useApi((c, signal) => residentMemories(c, { query: { userId: id }, signal }), dep);
  const directives = useApi((c, signal) => residentDirectives(c, { params: { id }, signal }), dep);
  const conversations = useApi((c, signal) => residentConversations(c, { query: { userId: id }, signal }), dep);
  const reminders = useApi((c, signal) => residentReminders(c, { query: { residentId: id }, signal }), dep);
  const sharing = useApi((c, signal) => residentFamilySharing(c, { params: { id }, query: { organizationId: ctx.persona === 'Operator' ? orgId : undefined }, signal }), dep);
  const ehr = useApi((c, signal) => ehrStatus(c, { query: { facility: orgId }, signal }), [orgId]);
  // Guardian is refused to the Partner (no Guardian page for that role), so the chart
  // does not ask for its signals.
  const signals = useApi((c, signal) => guardianSignals(c, { query: { open: true, limit: 100 }, signal }), [], { enabled: ctx.role !== 'PARTNER' });
  const vitals = useApi((c, signal) => residentVitals(c, { params: { id }, query: { windowHours: 336 }, signal }), dep);
  const adl = useApi((c, signal) => residentAdl(c, { params: { id }, signal }), dep);
  const condition = useApi((c, signal) => residentCondition(c, { params: { id }, signal }), dep);
  // Per tab.
  const onChart = tab === 'chart';
  const chart = useApi((c, signal) => residentChart(c, { params: { id }, signal }), dep, { enabled: tab === 'measures' });
  const score = useApi((c, signal) => residentScore(c, { params: { id }, signal }), dep, { enabled: onChart });
  const carePlan = useApi((c, signal) => residentCarePlan(c, { params: { id }, signal }), dep, { enabled: onChart });
  const mds = useApi((c, signal) => residentMds(c, { params: { id }, signal }), dep, { enabled: onChart });
  const pdpm = useApi((c, signal) => residentPdpm(c, { params: { id }, signal }), dep, { enabled: onChart });
  const timeline = useApi((c, signal) => residentTimeline(c, { params: { id }, query: { limit: 6 }, signal }), dep, { enabled: onChart });
  const transfers = useApi((c, signal) => residentTransfers(c, { params: { id }, signal }), dep, { enabled: onChart });
  const immunizations = useApi((c, signal) => residentImmunizations(c, { params: { id }, signal }), dep, { enabled: onChart });
  const acp = useApi((c, signal) => residentAcp(c, { params: { id }, signal }), dep, { enabled: onChart });
  // Restored from production (not in the design): the Part A flag behind the payer chip.
  const payerAdmin = ctx.persona === 'Facility Admin' && !ctx.readOnly;
  const partA = useApi((c, signal) => partAStay(c, { params: { id }, signal }), dep, { enabled: onChart && payerAdmin });
  const [payerMenu, setPayerMenu] = useState<{ top: number; left: number } | null>(null);
  const [dxOpen, setDxOpen] = useState(false);
  const [dxText, setDxText] = useState('');
  const [fhir, setFhir] = useState<{ busy: 'export' | 'push' | null; msg: string | null }>({ busy: null, msg: null });
  // V8/V9: the new medicine watch card (GET /residents/:id/med-watches). The Owner/Operator has
  // no clinical reads; a refusal or a missing route leaves the card off, never an error.
  const medWatches = useApi((c, signal) => residentMedWatches(c, { params: { id }, signal }).catch(() => null), dep, { enabled: ctx.role !== 'PARTNER' });
  const [mwUi, setMwUi] = useState<MedWatchUi>({ open: {} });
  // V8 Devices card: what is paired, and the consent question before connecting one.
  const devices = useApi((c, signal) => residentDevices(c, { query: { resident_id: id }, signal }).catch(() => null), dep);
  const [devAsk, setDevAsk] = useState<string | null>(null);
  const [devBusy, setDevBusy] = useState(false);

  const viewerName = session?.user.name ?? null;
  const writeMeta = (viewerName ?? 'You') + ' · just now';
  const addWrite = (kind: string, text: string) => setUiState((s) => ({ ...s, writes: s.writes.concat([{ kind, text, meta: writeMeta }]) }));
  const run = <T,>(pr: Promise<T>, then?: (r: T) => void) => {
    setErr(null);
    pr.then((r) => then && then(r)).catch((e: ApiError) => setErr(e));
  };

  // A section the server refuses this viewer (403, or 404 for a resident outside their reach)
  // says "Withheld for your role", never 0 or empty.
  const refused = (q: { data: unknown; error: ApiError | null }) => !q.data && (q.error?.kind === 'forbidden' || q.error?.kind === 'not_found');
  const withheld = { facesheet: refused(facesheet), meds: refused(meds), fall: refused(fall), vitals: refused(vitals), adl: refused(adl) };
  // Break-glass (decision 4): the Aithora team is refused clinical reads until the building
  // approves a request; the request button sits on that message, the banner shows an active one.
  const teamRestricted = [detail, meds, medsToday, facesheet, vitals, adl, condition, cognitive].some((q) => isTeamRestricted(q.error));
  const [accessTick, setAccessTick] = useState(0);

  const head = useMemo(
    () =>
      chartVals({
        persona: ctx.persona,
        readOnly: ctx.readOnly,
        withheld,
        viewerName,
        data: {
          watch: p.watch, rows: p.rows, row,
          detail: detail.data, facesheet: facesheet.data, fall: fall.data, interventions: interventions.data, cognitive: cognitive.data,
          meds: meds.data, medsToday: medsToday.data, memories: memories.data, directives: directives.data, conversations: conversations.data,
          reminders: reminders.data, sharing: sharing.data, ehr: ehr.data, signals: signals.data?.signals ?? null,
          vitals: vitals.data, adl: adl.data, condition: condition.data,
        },
        selId: id,
        focusMissing: p.focusMissing,
        tab,
        query: p.query,
        setQuery: p.setQuery,
        ui,
        nowMs,
        act: {
          setUi,
          select: p.onSelect,
          clearFocus: p.clearFocus,
          back: p.onBack,
          setTab: p.setTab,
          save: (mode, text) => {
            const c = ctx.client;
            const done = () => { addWrite(WRITE_KIND[mode as Exclude<ComposeMode, ''>], text); setUi({ compose: '', draft: '' }); };
            if (mode === 'note') run(residentTimelineAdd(c, { params: { id }, body: { body: text, kind: 'note' } }), () => { done(); timeline.reload(); });
            else if (mode === 'transfer') run(residentTransferAdd(c, { params: { id }, body: { reason: text } }), () => { done(); transfers.reload(); });
            else if (mode === 'escalate') run(residentEscalate(c, { params: { id }, body: { text } }), done);
            else if (mode === 'task') run(residentTaskOrder(c, { params: { id }, body: { text } }), done);
          },
          breakGlass: () => run(breakGlass(ctx.client, { body: { residentId: id, reason: 'Declared from the resident chart' } }), () => { setUi({ breakGlass: true }); addWrite('BREAK-GLASS', 'Break-glass read declared on this resident'); }),
          conditionAck: () => run(residentConditionAck(ctx.client, { params: { id }, body: {} }), () => condition.reload()),
          conditionClose: () => run(residentConditionClose(ctx.client, { params: { id }, body: {} }), () => condition.reload()),
          sbarSend: (f, carePath) =>
            run(residentSbar(ctx.client, { params: { id }, body: { situation: f.Situation, background: f.Background, assessment: f.Assessment, recommendation: f.Recommendation, carePath: carePath || undefined } }), () => condition.reload()),
          medGiven: (medicationId, dueAt, assisted) => {
            const m = meds.data?.medications.find((x) => x.id === medicationId);
            run(medPassAdminister(ctx.client, { body: { residentId: id, medicationId, scheduledFor: dueAt, status: 'GIVEN', ...(assisted ? { assisted } : {}) } }), () => {
              addWrite('MED', (m ? m.name + ' ' + (m.dosage ?? '') : 'Medication') + (assisted ? ' assisted, taken at ' : ' administered at ') + new Date(dueAt).toTimeString().slice(0, 5));
              medsToday.reload();
            });
          },
          logIntervention: (note) => run(mobilityInterventionAdd(ctx.client, { body: { residentId: id, interventionType: 'other', note } }), () => { setUi({ logNote: '' }); interventions.reload(); }),
          closeSignal: (sid, outcome) =>
            run(guardianSignalStep(ctx.client, { params: { id: sid }, body: { step: 'closed', note: outcome } }), () =>
              setUiState((s) => ({ ...s, signalOutcome: { ...s.signalOutcome, [sid]: { outcome, correct: null, at: 'just now' } } }))),
          signalFeedback: (sid, correct) =>
            run(guardianSignalFeedback(ctx.client, { params: { id: sid }, body: { correct } }), () =>
              setUiState((s) => ({ ...s, signalOutcome: { ...s.signalOutcome, [sid]: { ...s.signalOutcome[sid], correct } } }))),
          addDirective: (text, today) => {
            const end = new Date(nowMs);
            end.setHours(23, 59, 0, 0);
            run(residentDirectiveAdd(ctx.client, { params: { id }, body: { directive: text, expiresAt: today ? end.toISOString() : undefined } }), () => { setUi({ directive: '' }); directives.reload(); });
          },
          goIntegrations: () => navigate('/settings'),
          openLink: (to) => navigate(to),
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx, p.rows, p.watch, row, id, tab, p.query, ui, nowMs, detail.data, facesheet.data, fall.data, interventions.data, cognitive.data, meds.data, medsToday.data, memories.data,
      directives.data, conversations.data, reminders.data, sharing.data, ehr.data, signals.data, vitals.data, adl.data, condition.data, p.focusMissing,
      withheld.facesheet, withheld.meds, withheld.fall, withheld.vitals, withheld.adl],
  );

  const vit = vitalsVals({
    vitals: vitals.data, vitalsLoading: vitals.loading, adl: adl.data, adlError: !!adl.error && !adl.data,
    masked: !!row?.masked, reload: vitals.reload, reloadAdl: adl.reload, nowMs, withheld,
  });
  const mea = measuresVals({
    chart: chart.data, ui: mui, setUi: (x) => setMui((s) => ({ ...s, ...x })), canWrite: !ctx.readOnly, nowMs,
    capture: (measureId, drive) => run(residentMeasureCapture(ctx.client, { params: { id, measureId }, body: { state: drive ? 'recorded' : 'recorded' } }), () => chart.reload()),
  });
  const rec = recordVals({
    name: String(head.rSel),
    masked: !!row?.masked,
    facility: String(head.rSelFacility).split(' · ')[0],
    facesheet: facesheet.data, score: score.data, carePlan: carePlan.data, mds: mds.data, pdpm: pdpm.data, timeline: timeline.data, transfers: transfers.data,
    immunizations: immunizations.data, acp: acp.data, goalsDone, canWrite: !ctx.readOnly, nowMs, withheld,
    goalMet: (gid) => run(residentCarePlanGoalPatch(ctx.client, { params: { id, gid }, body: { state: 'met' } }), () => {
      setGoalsDone((g) => ({ ...g, [gid]: true }));
      const goal = carePlan.data?.carePlan.goals.find((g) => g.id === gid);
      addWrite('GOAL', 'Care-plan goal marked met: ' + (goal?.text ?? ''));
    }),
    vaxRecord: (vaccine) => run(residentImmunizationRecord(ctx.client, { params: { id }, body: { vaccine } }), () => { addWrite('VACCINE', vaccine + ' vaccination recorded'); immunizations.reload(); }),
  });

  // FHIR R4 (restored from production): the resident's Patient, Observations and CarePlan.
  const fhirResources = async (): Promise<FhirResource[]> => {
    const c = ctx.client;
    const [pt, obs, plan] = await Promise.all([fhirPatient(c, { params: { userId: id } }), fhirObservations(c, { params: { userId: id } }), fhirCarePlan(c, { params: { userId: id } })]);
    return [pt, obs, plan];
  };
  const exportBundle = async () => {
    setFhir({ busy: 'export', msg: null });
    try {
      const [pt, obs, plan] = await fhirResources();
      const entries = [pt, ...(((obs.entry as Array<{ resource: FhirResource }>) ?? []).map((x) => x.resource)), plan].map((resource) => ({ resource }));
      const bundle = { resourceType: 'Bundle', type: 'collection', timestamp: new Date(nowMs).toISOString(), entry: entries };
      const url = URL.createObjectURL(new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/fhir+json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fhir-' + id + '.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setFhir({ busy: null, msg: 'Bundle downloaded: ' + entries.length + ' resources.' });
    } catch (e) {
      setFhir({ busy: null, msg: null });
      setErr(e as ApiError);
    }
  };
  const PUSH_WHY: Record<string, string> = {
    ehr_push_disabled: 'EHR push is off for Aithora.',
    ehr_push_org_disabled: 'This building has not turned on EHR write-back.',
  };
  const pushToEhr = async () => {
    setFhir({ busy: 'push', msg: null });
    try {
      let sent = 0;
      const errors: string[] = [];
      for (const resource of await fhirResources()) {
        const r = await ehrPush(ctx.client, { body: { resource, organizationId: orgId } });
        sent += r.sent;
        errors.push(...r.errors);
      }
      const why = errors.length ? PUSH_WHY[errors[0]] ?? errors[0] : '';
      setFhir({ busy: null, msg: (sent ? 'Sent ' + sent + (sent === 1 ? ' resource' : ' resources') + ' to ' + (ehr.data?.vendor ?? 'the EHR') + '.' : 'Nothing was sent.') + (why ? ' ' + why : '') });
    } catch (e) {
      setFhir({ busy: null, msg: null });
      setErr(e as ApiError);
    }
  };
  const PAYERS: Array<[string, string]> = [['Medicare', 'Medicare'], ['Medicaid', 'Medicaid'], ['Dual eligible', 'dual'], ['Medicare Advantage', 'Medicare Advantage'], ['Private pay', 'private pay'], ['Clear the payer', '']];
  const partAOn = partA.data?.onCoveredStay === true;
  const payerItem = (text: string, onClick: () => void, on = false) => (
    <a key={text} href="#" role="menuitem" aria-current={on || undefined} onClick={(e) => { e.preventDefault(); onClick(); }}
      style={{ display: 'block', padding: '6px 9px', borderRadius: 6, fontSize: 16, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', color: on ? '#2F5C97' : '#22272E' }}>
      {text}
    </a>
  );
  const payerMenuNode = payerMenu ? (
    <>
      <span role="presentation" onClick={() => setPayerMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
      <span role="menu" aria-label="Payer" style={{ position: 'fixed', top: payerMenu.top, left: payerMenu.left, zIndex: 41, display: 'flex', flexDirection: 'column', minWidth: 200, padding: 4, borderRadius: 8, background: '#FCFAF4', border: '1px solid rgba(34,39,46,.16)', boxShadow: '0 8px 24px rgba(34,39,46,.14)' }}>
        {PAYERS.map(([text, value]) => payerItem(text, () => { setPayerMenu(null); run(residentPayerSet(ctx.client, { params: { id }, body: { payerType: value } }), () => facesheet.reload()); }))}
        <span style={{ height: 1, background: 'rgba(34,39,46,.1)', margin: '4px 0' }} />
        {payerItem(partA.data ? 'Part A covered stay: ' + (partAOn ? 'on' : 'off') : 'Part A covered stay', () => {
          setPayerMenu(null);
          if (partA.data) run(partAStaySet(ctx.client, { params: { id }, body: { active: !partAOn } }), () => partA.reload());
        }, partAOn)}
      </span>
    </>
  ) : null;
  const canPush = !ctx.readOnly && !!ehr.data?.canPush;

  // Family access: the resident's own sharing, edited here and saved with Save sharing.
  const shareOn = sharingDraft ?? (sharing.data ? Object.fromEntries(SHARE_KEYS.map((k) => [k, sharing.data!.sharing[k]])) : null);
  // The family-visible medical status and its note (the Flutter family access card's
  // editor, restored): chosen here and saved with Save sharing. undefined = unchanged.
  const statusCode = statusDraft !== undefined ? statusDraft : (sharing.data?.medicalStatusCode ?? null);
  const noteValue = noteDraft ?? sharing.data?.medicalStatusNote ?? '';
  const statusDirty = statusDraft !== undefined || noteDraft !== undefined;

  const V = {
    ...head,
    ...vit,
    ...mea,
    ...rec,
    rCogRing: head.rCogScore != null ? <CogRing score={head.rCogScore} outOf={30} /> : <span style={{ display: 'block', width: 78, height: 78, borderRadius: '50%', border: '4px solid rgba(34,39,46,.1)' }} />,
    rMocaRing: head.rMocaScore != null ? <CogRing score={head.rMocaScore} outOf={30} /> : null,
    rFamilyOn: shareOn ? SHARE_KEYS.map((k) => !!shareOn[k]) : head.rFamilyOn,
    rFamilyToggle: (i: number) => {
      if (ctx.readOnly || !shareOn) return;
      setSharingDraft({ ...shareOn, [SHARE_KEYS[i]]: !shareOn[SHARE_KEYS[i]] });
    },
    rFamilySave: (e?: { preventDefault?: () => void }) => {
      e?.preventDefault?.();
      if (ctx.readOnly || (!sharingDraft && !statusDirty)) return;
      const body = {
        ...(sharingDraft ? { toggles: sharingDraft } : {}),
        ...(statusDraft !== undefined ? { medicalStatusCode: statusDraft } : {}),
        ...(noteDraft !== undefined ? { medicalStatusNote: noteDraft.trim() } : {}),
      };
      run(residentFamilySharingSave(ctx.client, { params: { id }, body }), () => { setSharingDraft(null); setStatusDraft(undefined); setNoteDraft(undefined); sharing.reload(); });
    },
    rFamilyGenerate: (e?: { preventDefault?: () => void }) => {
      e?.preventDefault?.();
      if (ctx.readOnly || !orgId) return;
      run(residentFamilyCode(ctx.client, { params: { id }, body: { organizationId: orgId } }), () => sharing.reload());
    },
    rFamilyCodeText: sharing.data?.familyCode ?? 'No code yet',
    rMedicalStatus: statusCode ? sharing.data?.presets[statusCode] ?? statusCode : 'No status set',
    rFhirExport: row?.masked ? null : (e?: { preventDefault?: () => void }) => { e?.preventDefault?.(); if (!fhir.busy) void exportBundle(); },
    rFhirExportLabel: fhir.busy === 'export' ? 'Preparing' : 'Export bundle',
    rFhirPush: canPush ? (e?: { preventDefault?: () => void }) => { e?.preventDefault?.(); if (!fhir.busy) void pushToEhr(); } : null,
    rFhirPushLabel: fhir.busy === 'push' ? 'Sending' : 'Push to ' + (ehr.data?.vendor ?? 'the EHR') + ' →',
    rFhirMsg: fhir.msg,
    rPayerEdit: payerAdmin ? (e: { preventDefault: () => void; currentTarget: Element }) => {
      e.preventDefault();
      const r = e.currentTarget.getBoundingClientRect();
      setPayerMenu(payerMenu ? null : { top: Math.round(r.bottom + 4), left: Math.round(r.left) });
    } : null,
    rPayerMenu: payerMenuNode,
    rDxAdd: payerAdmin ? (e?: { preventDefault?: () => void }) => { e?.preventDefault?.(); setDxText(''); setDxOpen(true); } : null,
    rMedicalStatusSelect: sharing.data ? (
      <select
        aria-label="Medical status for family"
        value={statusCode ?? ''}
        disabled={ctx.readOnly}
        onChange={(e) => setStatusDraft((e.target.value || null) as typeof statusDraft)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: ctx.readOnly ? 'default' : 'pointer', font: 'inherit' }}
      >
        <option value="">No status set</option>
        {Object.entries(sharing.data.presets).map(([k, label]) => (
          <option key={k} value={k}>
            {label}
          </option>
        ))}
      </select>
    ) : null,
    rMedicalNoteInput: sharing.data ? (
      <input
        type="text"
        className="chart-note-input"
        aria-label="Note for family"
        placeholder="Optional note for family (no clinical detail)"
        value={noteValue}
        readOnly={ctx.readOnly}
        maxLength={280}
        onChange={(e) => setNoteDraft(e.target.value)}
        style={{ flex: 1, minWidth: 0, height: '100%', border: 0, padding: 0, margin: 0, background: 'transparent', font: 'inherit', color: '#22272E', outline: 'none' }}
      />
    ) : null,
    // Restored from production (not in the design): reminders writes, wearables, the
    // health card, and the Life Story as its PDF (the design's link printed the chart).
    rMedControls: <MedicationAdd client={ctx.client} residentId={id} organizationId={orgId ?? null} readOnly={ctx.readOnly} meds={meds.data?.medications ?? []} onAdded={() => { meds.reload(); medsToday.reload(); }} />,
    rReminderControls: (
      <ReminderControls
        client={ctx.client}
        residentId={id}
        reminders={(reminders.data?.reminders ?? []).map((r) => ({ id: String(r.id), title: r.title, kind: String(r.kind), status: String(r.status) }))}
        readOnly={ctx.readOnly}
        onChanged={() => reminders.reload()}
      />
    ),
    rChartExtras: <HealthCardCard client={ctx.client} residentId={id} />,
    ...medWatchVals({
      data: medWatches.data ?? null,
      canAct: !ctx.readOnly,
      // Care staff and the Facility Admin report signs (POST /med-watches/:id/signs).
      canReport: !ctx.readOnly && (ctx.persona === 'Care Staff' || ctx.persona === 'Facility Admin'),
      nowMs,
      ui: mwUi,
      act: {
        setOpen: (wid, open) => setMwUi((s) => ({ open: { ...s.open, [wid]: open } })),
        done: (w, step) => run(medWatchStepDone(ctx.client, { params: { id: w.id, stepId: step.id }, body: {} }), () => medWatches.reload()),
        close: (w) => run(medWatchClose(ctx.client, { params: { id: w.id }, body: {} }), () => medWatches.reload()),
        report: (wid, sign) => run(medWatchSign(ctx.client, { params: { id: wid }, body: { sign } }), () => { medWatches.reload(); signals.reload(); }),
      },
    }),
    ...devicesVals({
      data: devices.data ?? null,
      name: String(head.rSel),
      canAct: !ctx.readOnly && !row?.masked,
      isAdmin: ctx.persona === 'Facility Admin',
      asking: devAsk,
      busy: devBusy,
      act: {
        ask: setDevAsk,
        connect: (provider) => {
          setDevBusy(true);
          const open = (url: string) => { if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener'); };
          const go = provider === 'junction'
            ? junctionLink(ctx.client, { body: { resident_id: id } }).then((r) => open(r.linkUrl))
            : deviceConnect(ctx.client, { params: { provider }, query: { resident_id: id } }).then((r) => open(r.authorizeUrl));
          run(go.finally(() => setDevBusy(false)), () => { setDevAsk(null); devices.reload(); });
        },
        remove: (provider) => {
          setDevBusy(true);
          run(deviceDisconnect(ctx.client, { params: { provider }, query: { resident_id: id } }).finally(() => setDevBusy(false)), () => devices.reload());
        },
        openIntegrations: () => navigate('/settings?section=Integrations'),
      },
    }),
    ...veteranVals(),
    rLifeStoryPdf: (e: { preventDefault?: () => void }) => {
      e?.preventDefault?.();
      run((async () => {
        const res = await ctx.client.raw(familyLifeStoryPdfPath(id));
        if (!res.ok) {
          let msg = `The Life Story could not be opened (${res.status}).`;
          try { const j = await res.json(); if (j?.error) msg = String(j.error); } catch { /* not JSON */ }
          throw new Error(msg);
        }
        const url = URL.createObjectURL(await res.blob());
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })() as Promise<unknown>);
    },
    // Aithora never takes family access (the server refuses its team both panels), so they
    // are not asked for: only a building's own staff manage a resident's family.
    rFamilyRequests: team ? null : (
      <>
      <FamilyLinks client={ctx.client} residentId={id} orgId={orgId} readOnly={ctx.readOnly} />
      <FamilyRequests
        client={ctx.client}
        residentId={id}
        orgId={orgId}
        toggles={shareOn ? Object.fromEntries(SHARE_KEYS.map((k) => [k, !!shareOn[k]])) : null}
        readOnly={ctx.readOnly}
        onDecided={() => sharing.reload()}
      />
      </>
    ),
  };

  return (
    <>
      {/* Keyed on the server's answer, not the persona: a team member viewing as a facility role
          is still refused by their real role, and still needs the request button. */}
      {/* The server answers "no grant" for everyone else, so the banner shows only for a team member. */}
      <TeamAccessBanner key={accessTick} client={ctx.client} orgId={orgId ?? null} residentId={id} buildingName={row?.facilityName ?? null} residentName={row?.name ?? null} onEnded={() => setAccessTick((t) => t + 1)} />
      {teamRestricted && (
        <TeamAccessRequest
          client={ctx.client}
          orgId={orgId ?? null}
          residentId={id}
          buildingName={row?.facilityName ?? null}
          residentName={row?.name ?? null}
          // Emergency access starts at once: re-read the chart so it opens without a reload.
          onSent={() => { setAccessTick((t) => t + 1); for (const q of [detail, meds, medsToday, facesheet, vitals, adl, condition, cognitive]) q.reload(); }}
        />
      )}
      <ChartPaneView V={V} />
      {err && <ErrorState error={err} />}
      {dxOpen && (
        <ConfirmDialog
          title="Add a diagnosis"
          confirmLabel="Add"
          reason={{ label: 'ICD-10 code', placeholder: 'For example E11.9 (required)', showLabel: true }}
          body={
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 14, fontWeight: 600, color: '#54606C' }}>
              <span>What it is (optional)</span>
              <input value={dxText} onChange={(e) => setDxText(e.target.value)} placeholder="Type 2 diabetes"
                style={{ boxSizing: 'border-box', width: '100%', minHeight: 34, padding: '0 12px', border: '1px solid rgba(34,39,46,.18)', borderRadius: 8, background: '#FBF8F1', fontSize: 16, color: '#22272E', fontFamily: 'inherit' }} />
            </label>
          }
          onConfirm={async (code) => {
            await residentDiagnosisAdd(ctx.client, { params: { id }, body: { icd10: code, description: dxText.trim() || undefined } });
            facesheet.reload();
          }}
          onClose={() => setDxOpen(false)}
        />
      )}
    </>
  );
}
